import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, Minus, Plus } from "lucide-react";
import { fetchProduct, fetchProducts } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { formatINR } from "@/lib/format";
import Seo, { SITE_URL } from "@/components/seo/Seo";
import WaitlistModal from "@/components/WaitlistModal";

export default function Product() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [bundleItems, setBundleItems] = useState([]);
  const [qty, setQty] = useState(1);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const { add } = useCart();

  useEffect(() => {
    fetchProduct(slug).then(async (p) => {
      setProduct(p);
      if (p?.bundle_of?.length) {
        const all = await fetchProducts();
        const map = Object.fromEntries(all.map((x) => [x.slug, x]));
        setBundleItems(p.bundle_of.map((s) => map[s]).filter(Boolean));
      } else {
        setBundleItems([]);
      }
    }).catch(() => {});
    fetchProducts().then((list) => setRelated(list.filter((p) => p.slug !== slug && p.slug !== "the-rest-set").slice(0, 3)));
  }, [slug]);

  if (!product) {
    return (
      <div className="pt-40 px-6 md:px-12 lg:px-16 min-h-[60vh]">
        <p className="overline text-stone">Loading…</p>
      </div>
    );
  }

  return (
    <div data-testid="product-page" className="pt-28 md:pt-32">
      <Seo
        path={`/shop/${product.slug}`}
        title={product.name}
        description={product.tagline || product.description?.slice(0, 160)}
        image={product.cover_image}
        type="product"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.name,
          description: product.description,
          image: product.cover_image
            ? (product.cover_image.startsWith("http")
                ? product.cover_image
                : `${SITE_URL}${product.cover_image}`)
            : undefined,
          sku: product.slug,
          brand: { "@type": "Brand", name: "Still Form" },
          category: product.category,
          offers: {
            "@type": "Offer",
            url: `${SITE_URL}/shop/${product.slug}`,
            priceCurrency: product.currency || "INR",
            price: product.price,
            availability: "https://schema.org/InStock",
            itemCondition: "https://schema.org/NewCondition",
          },
        }}
      />
      <div className="px-6 md:px-12 lg:px-16 pb-12">
        <Link to="/shop" className="overline inline-flex items-center gap-2 text-stone hover:text-ink mb-10">
          <ArrowLeft size={12} /> All Objects
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7 img-zoom aspect-[4/5] overflow-hidden bg-clay">
            <img src={product.cover_image} alt={product.name} className="w-full h-full object-cover" />
          </div>

          <div className="lg:col-span-5 lg:pt-10">
            <p className="overline mb-4">{product.category}</p>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-light leading-[0.98] tracking-[-0.02em] text-ink">
              {product.name}
            </h1>
            <p className="font-display italic text-xl md:text-2xl text-charcoal mt-3">{product.tagline}</p>

            <p className="mt-8 text-base md:text-lg text-charcoal font-light leading-[1.7] max-w-md">
              {product.description}
            </p>

            <div className="mt-10 border-t border-hairline pt-6 flex items-center justify-between">
              {product.is_waitlist ? (
                <p className="overline text-moss">Coming soon · Waitlist</p>
              ) : (
                <p className="font-display text-3xl text-ink">{formatINR(product.price)}</p>
              )}
              {!product.is_waitlist && (
                <div className="flex items-center gap-3 border border-hairline px-3 py-2">
                  <button
                    data-testid="qty-minus"
                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                    className="text-charcoal hover:text-ink"
                    aria-label="Decrease quantity"
                  >
                    <Minus size={14} />
                  </button>
                  <span className="font-mono text-sm w-6 text-center">{qty}</span>
                  <button
                    data-testid="qty-plus"
                    onClick={() => setQty((q) => q + 1)}
                    className="text-charcoal hover:text-ink"
                    aria-label="Increase quantity"
                  >
                    <Plus size={14} />
                  </button>
                </div>
              )}
            </div>

            {product.is_waitlist ? (
              <button
                data-testid="product-waitlist-cta"
                onClick={() => setShowWaitlist(true)}
                className="mt-6 w-full flex items-center justify-center gap-3 text-[0.72rem] tracking-[0.22em] uppercase font-medium text-ink border border-ink py-5 hover:bg-ink hover:text-bone transition-colors"
              >
                Join the waitlist <ArrowUpRight size={14} />
              </button>
            ) : (
              <button
                data-testid="add-to-cart"
                onClick={() => {
                  add(product, qty);
                  toast(`${product.name} held in the bag`);
                }}
                className="mt-6 w-full flex items-center justify-center gap-3 text-[0.72rem] tracking-[0.22em] uppercase font-medium text-bone bg-ink py-5 hover:bg-moss transition-colors"
              >
                Add to bag <ArrowUpRight size={14} />
              </button>
            )}

            <div className="mt-10 grid grid-cols-2 gap-6 border-t border-hairline pt-6">
              <div>
                <p className="overline mb-3">Composition</p>
                <ul className="space-y-1 text-sm text-charcoal">
                  {product.ingredients.map((i) => (
                    <li key={i} className="italic font-display text-base">{i}</li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="overline mb-3">Notes</p>
                <ul className="space-y-1 text-sm text-charcoal">
                  {product.notes.map((n) => (
                    <li key={n} className="italic font-display text-base">{n}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* What's in the set — only for products with bundle_of */}
      {bundleItems.length > 0 && (
        <section
          className="px-6 md:px-12 lg:px-16 py-20 md:py-24 border-t border-hairline bg-clay/40"
          data-testid="set-contents"
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 items-end">
            <div className="lg:col-span-7">
              <p className="overline mb-4 text-moss">Inside the set</p>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light leading-[1.0] tracking-tight text-ink">
                Five objects, held together.
              </h2>
            </div>
            <p className="lg:col-span-4 lg:col-start-9 text-charcoal text-base md:text-lg font-light leading-[1.7]">
              Each piece is the same as the one sold on its own. The set adds a
              custom Stillform pouch — bone-coloured linen, hand-stitched —
              designed to hold and organise your nightly ritual.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-10">
            {bundleItems.map((b, i) => (
              <Link
                key={b.slug}
                to={`/shop/${b.slug}`}
                data-testid={`set-content-${b.slug}`}
                className="group"
              >
                <div className="img-zoom aspect-[4/5] overflow-hidden bg-bone">
                  <img src={b.cover_image} alt={b.name} className="w-full h-full object-cover" />
                </div>
                <p className="overline mt-3 mb-1">{String(i + 1).padStart(2, "0")} · {b.stage || b.category}</p>
                <h4 className="font-display text-lg md:text-xl text-ink leading-tight group-hover:italic transition-all">
                  {b.name}
                </h4>
              </Link>
            ))}
          </div>

          {/* The pouch — listed as a sixth content card, framed not as a gift */}
          <div className="mt-12 border-t border-hairline pt-8 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
            <p className="overline lg:col-span-2 text-moss">+ The pouch</p>
            <p className="lg:col-span-10 font-display italic text-xl md:text-2xl text-charcoal leading-snug">
              A custom Stillform pouch, designed to hold and organise your nightly ritual.
            </p>
          </div>
        </section>
      )}

      <section className="px-6 md:px-12 lg:px-16 py-20 border-t border-hairline">
        <p className="overline mb-10">Of a kind</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {related.map((p) => (
            <Link to={`/shop/${p.slug}`} key={p.slug} className="group" data-testid={`related-product-${p.slug}`}>
              <div className="img-zoom aspect-[4/5] overflow-hidden bg-clay">
                <img src={p.cover_image} alt={p.name} className="w-full h-full object-cover" />
              </div>
              <div className="pt-4 flex items-start justify-between">
                <div>
                  <p className="overline mb-1">{p.category}</p>
                  <h3 className="font-display text-xl text-ink group-hover:italic transition-all">{p.name}</h3>
                </div>
                <p className="text-xs font-mono text-terracotta tracking-wider">
                  {p.is_waitlist ? "Dec 2026" : formatINR(p.price)}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <WaitlistModal
        open={showWaitlist}
        onClose={() => setShowWaitlist(false)}
        product={product}
      />
    </div>
  );
}
